import React, { useState, useEffect } from 'react';
import { CheckSquare, Square, CurrencyCircleDollar, PaperPlaneTilt, Spinner } from '@phosphor-icons/react';
import axios from '../../utils/axiosInstance';
import PrzelewGotowkaView from './PrzelewGotowkaView';

const API = `${window.location.origin}/api`;

const MONTHS = [
  'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
  'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'
];

const YEARS = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i);

// Real-time calculation of przelew (same formula as Wynagrodzenia tab)
// Stored `przelew` field can be stale if paliwo_items / custom_deductions
// were edited inline without re-saving the whole record.
const getActualPrzelew = (record) => {
  const basePaliwo = parseFloat(record.paliwo) || 0;
  const itemsPaliwo = (record.paliwo_items || []).reduce(
    (sum, v) => sum + (parseFloat(v) || 0),
    0,
  );
  const customDeductionsTotal = (record.custom_deductions || []).reduce(
    (sum, d) => sum + (parseFloat(d.amount) || 0),
    0,
  );
  return (
    (parseFloat(record.dochod) || 0) -
    basePaliwo -
    itemsPaliwo -
    (parseFloat(record.nota) || 0) -
    (parseFloat(record.rata) || 0) -
    customDeductionsTotal
  );
};

export default function PaymentListTab() {
  const [salaries, setSalaries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [view, setView] = useState('lista');

  // Default: shift to previous month when we're before day 16
  const _now = new Date();
  const _defaultShift = _now.getDate() < 16 ? -1 : 0;
  const _defaultDate = new Date(_now.getFullYear(), _now.getMonth() + _defaultShift, 1);

  // Filters
  const [year, setYear] = useState(_defaultDate.getFullYear());
  const [month, setMonth] = useState(_defaultDate.getMonth() + 1);
  // Single period selector: "01-15" or "16-30"
  const [period, setPeriod] = useState('01-15');
  const periodStart = period === '01-15' ? '01' : '16';
  const periodEnd = period === '01-15' ? '15' : '30';

  // Email-notification state
  const [sending, setSending] = useState(false);
  const [notifyResult, setNotifyResult] = useState(null);

  useEffect(() => {
    fetchSalaries();
  }, [year, month, period]);

  const fetchSalaries = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/salary/all?year=${year}&month=${month}&period_start=${periodStart}&period_end=${periodEnd}`
      );
      setSalaries(response.data);
      // Selections are persisted on the server (payment_selected)
      setSelectedIds(new Set((response.data || []).filter((s) => s.payment_selected).map((s) => s.id)));
    } catch (err) {
      console.error('Error fetching salaries:', err);
      alert('Błąd podczas pobierania danych');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelection = (salaryId) => {
    const prev = selectedIds;
    const newSelected = new Set(selectedIds);
    const on = !newSelected.has(salaryId);
    if (on) newSelected.add(salaryId); else newSelected.delete(salaryId);
    setSelectedIds(newSelected);
    axios.patch(`${API}/payouts/${salaryId}`, { payment_selected: on }).catch(() => {
      setSelectedIds(prev);
      alert('Nie udało się zapisać zaznaczenia');
    });
  };

  const toggleSelectAll = () => {
    const prev = selectedIds;
    const on = !(selectedIds.size === salaries.length && salaries.length > 0);
    setSelectedIds(on ? new Set(salaries.map((s) => s.id)) : new Set());
    axios.post(`${API}/payouts/select`, { ids: salaries.map((s) => s.id), selected: on }).catch(() => {
      setSelectedIds(prev);
      alert('Nie udało się zapisać zaznaczenia');
    });
  };

  const onRecordUpdated = (upd) => setSalaries((prev) => prev.map((s) => (s.id === upd.id ? { ...s, ...upd, split_przelew: upd.split_przelew ?? null } : s)));

  // Calculate total of selected salaries (live formula)
  const selectedTotal = salaries
    .filter(s => selectedIds.has(s.id))
    .reduce((sum, s) => sum + getActualPrzelew(s), 0);

  const allSelected = salaries.length > 0 && selectedIds.size === salaries.length;

  const sendNotifications = async () => {
    if (selectedIds.size === 0) {
      alert('Najpierw zaznacz wynagrodzenia, dla których chcesz wysłać powiadomienia.');
      return;
    }
    if (!window.confirm(`Wysłać powiadomienia push do ${selectedIds.size} pracowników?`)) {
      return;
    }
    setSending(true);
    setNotifyResult(null);
    try {
      const res = await axios.post(`${API}/salary/notify-payment`, {
        salary_ids: Array.from(selectedIds),
      });
      setNotifyResult(res.data);
    } catch (err) {
      console.error('Notify failed:', err);
      const msg = err.response?.data?.detail || 'Błąd podczas wysyłania powiadomień';
      alert(msg);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12">Ładowanie...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-muted rounded-sm p-6 shadow-md border border-border">
        <h3 className="text-lg font-bold text-slate-800 mb-4">Filtry</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Rok</label>
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {YEARS.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Miesiąc</label>
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {MONTHS.map((m, idx) => (
                <option key={idx} value={idx + 1}>{m}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Okres</label>
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              data-testid="payment-period-select"
            >
              <option value="01-15">01-15</option>
              <option value="16-30">16-30</option>
            </select>
          </div>
        </div>
      </div>

      <div className="flex gap-2" data-testid="payment-view-switch">
        {[['lista', 'Lista wypłat'], ['split', 'Przelew i gotówka']].map(([k, l]) => (
          <button
            key={k}
            onClick={() => setView(k)}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${view === k ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
            data-testid={`payment-view-${k}`}
          >
            {l}
          </button>
        ))}
      </div>

      {view === 'split' ? (
        salaries.length === 0 ? (
          <div className="text-center py-16 text-gray-500">Brak wynagrodzeń do wypłaty w tym okresie</div>
        ) : (
          <PrzelewGotowkaView salaries={salaries} getDoWyplaty={getActualPrzelew} onRecordUpdated={onRecordUpdated} />
        )
      ) : (<>
      {/* Payment List */}
      <div className="bg-white rounded-sm shadow-lg border border-gray-100 overflow-hidden">
        <div className="bg-muted p-6 text-white">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold">Lista wypłat</h2>
              <p className="text-green-100 mt-1">
                {MONTHS[month - 1]} {year}, okres {periodStart}-{periodEnd}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="text-sm text-green-100">Wybrano</div>
                <div className="text-3xl font-bold">{selectedIds.size} / {salaries.length}</div>
              </div>
              <button
                data-testid="send-notifications-btn"
                onClick={sendNotifications}
                disabled={sending || selectedIds.size === 0}
                className={`px-5 py-3 rounded-xl font-semibold flex items-center gap-2 transition-all shadow-md ${
                  sending || selectedIds.size === 0
                    ? 'bg-white/30 text-white/70 cursor-not-allowed'
                    : 'bg-white text-emerald-700 hover:bg-emerald-50 hover:shadow-lg'
                }`}
              >
                {sending ? (
                  <>
                    <Spinner className="w-5 h-5 animate-spin" weight="bold" />
                    Wysyłanie…
                  </>
                ) : (
                  <>
                    <PaperPlaneTilt className="w-5 h-5" weight="bold" />
                    Wyślij powiadomienia
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {notifyResult && (
          <div
            className={`px-6 py-4 border-b ${
              notifyResult.success_count === notifyResult.total
                ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                : 'bg-amber-50 border-amber-200 text-amber-900'
            }`}
            data-testid="notify-result-banner"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-semibold">{notifyResult.message}</p>
                {notifyResult.results && notifyResult.results.some((r) => !r.success) && (
                  <ul className="text-sm mt-2 list-disc list-inside space-y-1">
                    {notifyResult.results
                      .filter((r) => !r.success)
                      .map((r, i) => (
                        <li key={i}>
                          <strong>{r.employee || r.salary_id}</strong>: {r.error}
                        </li>
                      ))}
                  </ul>
                )}
              </div>
              <button
                onClick={() => setNotifyResult(null)}
                className="text-sm underline opacity-70 hover:opacity-100"
              >
                Zamknij
              </button>
            </div>
          </div>
        )}

        {salaries.length === 0 ? (
          <div className="text-center py-16">
            <CurrencyCircleDollar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">Brak wynagrodzeń do wypłaty w tym okresie</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="py-4 px-6 text-left">
                      <button
                        onClick={toggleSelectAll}
                        className="flex items-center gap-2 hover:text-foreground transition-colors"
                      >
                        {allSelected ? (
                          <CheckSquare className="w-6 h-6 text-foreground" weight="fill" />
                        ) : (
                          <Square className="w-6 h-6" />
                        )}
                        <span className="text-xs font-bold uppercase tracking-wider text-slate-600">
                          Zaznacz wszystkie
                        </span>
                      </button>
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-bold uppercase tracking-wider text-slate-600">
                      Skrót
                    </th>
                    <th className="text-right py-4 px-6 text-xs font-bold uppercase tracking-wider text-slate-600">
                      Przelew
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {salaries.map((salary, idx) => (
                    <tr
                      key={salary.id}
                      onClick={() => toggleSelection(salary.id)}
                      className={`cursor-pointer hover:bg-muted transition-colors duration-200 ${
                        idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'
                      } ${selectedIds.has(salary.id) ? 'bg-muted border-l-4 border-foreground' : ''}`}
                    >
                      <td className="py-4 px-6 border-b border-gray-100">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSelection(salary.id);
                          }}
                          className="focus:outline-none"
                        >
                          {selectedIds.has(salary.id) ? (
                            <CheckSquare className="w-6 h-6 text-foreground" weight="fill" />
                          ) : (
                            <Square className="w-6 h-6 text-gray-400 hover:text-blue-500" />
                          )}
                        </button>
                      </td>
                      <td className="py-4 px-6 border-b border-gray-100">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-foreground rounded-lg flex items-center justify-center">
                            <span className="text-white font-bold text-sm">
                              {salary.employee_name ? salary.employee_name.substring(0, 2).toUpperCase() : '??'}
                            </span>
                          </div>
                          <span className="font-bold text-lg text-slate-800">
                            {salary.employee_name || 'Nieznany'}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-6 border-b border-gray-100 text-right">
                        <div className="text-2xl font-bold text-green-600">
                          {getActualPrzelew(salary).toLocaleString('pl-PL', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })} zł
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Total Summary */}
            <div className="bg-muted p-6 text-white border-t-4 border-green-400">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-green-100">Suma do wypłaty</h3>
                  <p className="text-sm text-green-100 mt-1">
                    {selectedIds.size} {selectedIds.size === 1 ? 'pracownik' : selectedIds.size < 5 ? 'pracowników' : 'pracowników'} zaznaczonych
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-5xl font-bold">
                    {selectedTotal.toLocaleString('pl-PL', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })} zł
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Quick Stats */}
      {salaries.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-foreground rounded-sm p-6 text-white shadow-lg">
            <div className="flex items-center gap-3 mb-2">
              <CurrencyCircleDollar className="w-8 h-8" weight="bold" />
              <h3 className="text-lg font-semibold">Całkowita suma</h3>
            </div>
            <p className="text-3xl font-bold">
              {salaries.reduce((sum, s) => sum + getActualPrzelew(s), 0).toLocaleString('pl-PL', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })} zł
            </p>
            <p className="text-sm text-muted-foreground mt-1">Wszystkie wynagrodzenia</p>
          </div>
          
          <div className="bg-muted rounded-sm p-6 text-white shadow-lg">
            <div className="flex items-center gap-3 mb-2">
              <CheckSquare className="w-8 h-8" weight="bold" />
              <h3 className="text-lg font-semibold">Zaznaczone</h3>
            </div>
            <p className="text-3xl font-bold">
              {selectedTotal.toLocaleString('pl-PL', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })} zł
            </p>
            <p className="text-sm text-purple-100 mt-1">{selectedIds.size} pracowników</p>
          </div>
          
          <div className="bg-muted rounded-sm p-6 text-white shadow-lg">
            <div className="flex items-center gap-3 mb-2">
              <Square className="w-8 h-8" weight="bold" />
              <h3 className="text-lg font-semibold">Pozostałe</h3>
            </div>
            <p className="text-3xl font-bold">
              {(salaries.reduce((sum, s) => sum + getActualPrzelew(s), 0) - selectedTotal).toLocaleString('pl-PL', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })} zł
            </p>
            <p className="text-sm text-orange-100 mt-1">{salaries.length - selectedIds.size} pracowników</p>
          </div>
        </div>
      )}
      </>)}
    </div>
  );
}
